import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Phone, List, X, CaretDown } from "@phosphor-icons/react";
import { FEATURED_GUIDES } from "@/seoContentV5";

const LOGO_COLOR = "/cleanout247-logo.webp";

const NAV = [
  { label: "Services", id: "services" },
  { label: "Estimate", id: "estimate" },
  { label: "Pricing", path: "/junk-removal-cost" },
  { label: "How It Works", path: "/about" },
  { label: "Our Work", path: "/our-work" },
  { label: "Service Areas", path: "/service-areas" },
  { label: "Guides", path: "/blog" },
  { label: "Reviews", path: "/reviews" },
  { label: "FAQ", path: "/faq" },
  { label: "Contact", path: "/contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const go = (n) => {
    setOpen(false);
    if (n.path) {
      navigate(n.path);
      return;
    }
    const el = document.getElementById(n.id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => document.getElementById(n.id)?.scrollIntoView({ behavior: "smooth" }), 350);
    }
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 bg-white/95 backdrop-blur-xl border-b border-[#E4E4E7]"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-2 flex items-center justify-between gap-4 relative">
        <p
          data-testid="header-slogan"
          className="hidden md:block absolute bottom-1.5 left-1/2 -translate-x-1/2 font-display font-semibold italic text-xs sm:text-sm tracking-wide whitespace-nowrap"
        >
          <span className="text-[#15306E]">We Like It Clean.</span> <span className="text-[#46A01E]">Just Like You Do.</span>
        </p>
        <button
          data-testid="logo-button"
          onClick={() => navigate("/")}
          className="flex items-center shrink-0"
        >
          <img
            src={LOGO_COLOR}
            alt="CleanOut247 logo"
            width={520}
            height={363}
            className="h-16 sm:h-24 lg:h-44 w-auto"
          />
        </button>

        <nav className="hidden xl:flex items-center gap-4 2xl:gap-7">
          {NAV.map((n) => n.path === "/blog" ? (
            <div key={n.path} className="relative group">
              <button data-testid="nav-blog" onClick={() => go(n)} aria-haspopup="true"
                className="flex items-center gap-1 whitespace-nowrap text-sm font-medium tracking-wide text-[#15306E] hover:text-[#46A01E] transition-colors">
                {n.label} <CaretDown size={12} weight="bold" />
              </button>
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 hidden group-hover:block group-focus-within:block z-50" data-testid="nav-guides-menu">
                <div className="bg-white border border-[#E4E4E7] rounded-sm shadow-lg py-2 w-64">
                  {FEATURED_GUIDES.map((g) => (
                    <Link key={g.to} to={g.to} data-testid={`nav-guide-${g.to.split("/").pop()}`} className="block px-4 py-2 text-sm text-[#15306E] hover:bg-[#F4FAF0] hover:text-[#46A01E]">{g.label}</Link>
                  ))}
                  <Link to="/blog" data-testid="nav-guide-all" className="block px-4 py-2 mt-1 border-t border-[#F4F4F5] text-sm font-bold text-[#46A01E] hover:bg-[#F4FAF0]">All guides →</Link>
                </div>
              </div>
            </div>
          ) : (
            <button
              key={n.id || n.path}
              data-testid={`nav-${n.id || n.path.split("/").filter(Boolean).join("-")}`}
              onClick={() => go(n)}
              className="whitespace-nowrap text-sm font-medium tracking-wide text-[#15306E] hover:text-[#46A01E] transition-colors"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <a
            href="tel:+16199993565"
            data-testid="header-call-button"
            className="hidden sm:flex items-center gap-2 bg-[#15306E] text-white px-4 py-2 rounded-sm font-display font-bold text-sm hover:bg-[#46A01E] transition-colors"
          >
            <Phone size={16} weight="fill" />
            <span className="flex flex-col leading-tight text-center">
              <span className="whitespace-nowrap">Call/Text</span>
              <span className="whitespace-nowrap">(619) 999-3565</span>
            </span>
          </a>
          <button
            data-testid="mobile-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className="xl:hidden p-2 rounded-md text-[#15306E]"
          >
            {open ? <X size={26} /> : <List size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-white border-t border-[#E4E4E7] px-5 py-4 pb-24 md:pb-4 space-y-1 max-h-[calc(100dvh-5rem)] overflow-y-auto overscroll-contain" data-testid="mobile-menu-panel">
          {NAV.map((n) => (
            <div key={n.id || n.path}>
              <button
                data-testid={`mobile-nav-${n.id || n.path.split("/").filter(Boolean).join("-")}`}
                onClick={() => go(n)}
                className="block w-full text-left py-2.5 text-[#15306E] font-medium border-b border-[#F4F4F5]"
              >
                {n.label}
              </button>
              {n.path === "/blog" && (
                <div className="pl-4 py-1 border-b border-[#F4F4F5]" data-testid="mobile-guides-links">
                  {FEATURED_GUIDES.map((g) => (
                    <Link key={g.to} to={g.to} onClick={() => setOpen(false)} className="block py-1.5 text-sm text-[#52525B]">{g.label}</Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <a
            href="tel:+16199993565"
            className="mt-3 flex items-center justify-center gap-2 bg-[#46A01E] text-white px-4 py-3 rounded-sm font-display font-bold"
          >
            <Phone size={18} weight="fill" /> Call/Text (619) 999-3565
          </a>
        </div>
      )}
    </header>
  );
}
